import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { KPI } from '../../../../@core/api';
import { NbDialogRef, NbCardModule, NbSpinnerModule, NbSelectModule, NbOptionModule, NbInputModule, NbButtonModule, NbToastrService } from '@nebular/theme';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule, AsyncPipe } from '@angular/common';
import { EnergyObjectivesService } from '../../../../@core/utils/energy-objectives.service';
import { PerimetersService } from '../../../../@core/utils/perimeters.service';
import { ChartsService } from '../../../../@core/utils/charts.service';
import { ScopeService } from '../../../../@core/utils/scope.service';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { map, tap } from 'rxjs/operators';

interface EnergyObjective {
  id: number;
  building: string;
  year: number;
  surface: number;
  objectiveType: string;
  objectiveValue: number;
}

@Component({
  selector: 'app-edit-energy-objective-dialog',
  templateUrl: './edit-energy-objective-dialog.component.html',
  styleUrls: ['./edit-energy-objective-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NbCardModule,
    NbSpinnerModule,
    NbSelectModule,
    NbOptionModule,
    NbInputModule,
    NbButtonModule,
    AsyncPipe,
    TranslateModule
  ],
})
export class EditEnergyObjectiveDialogComponent implements OnInit, OnDestroy {
  @Input() public perimeter: KPI.Perimeter;
  @Input() public objective: EnergyObjective;

  public loading = false;
  private subscriptions: Subscription[] = [];
  public subPerimeters: any[] = [];
  public perimeters$: Observable<KPI.PerimeterConsumption[]>;
  
  public form = new UntypedFormGroup({
    site: new UntypedFormControl({ value: '', disabled: true }, [Validators.required]),
    perimeter: new UntypedFormControl(null, [Validators.required]),
    year: new UntypedFormControl('2030', [Validators.required]),
    objective_type: new UntypedFormControl({ value: this.translate.instant('Site kWh objective'), disabled: true }, [Validators.required]),
    objective_value: new UntypedFormControl(null, [Validators.required, Validators.min(0)]),
  });

  constructor(
    private dialogRef: NbDialogRef<EditEnergyObjectiveDialogComponent>,
    private energyObjectivesService: EnergyObjectivesService,
    private toastrService: NbToastrService,
    private translate: TranslateService,
    private perimetersService: PerimetersService,
    private chartsService: ChartsService,
    private scopeService: ScopeService,
  ) { }

  ngOnInit(): void {
    this.form.get('site').setValue(this.perimeter.name);
    this.subPerimeters = this.perimeter.sub_perimeters.filter(sub=>!sub?.level_parent) || [];
    this.form.get('objective_type').setValue(this.translate.instant('Site kWh objective'));
    
    if (this.objective.year) {
      this.form.patchValue({ year: this.objective.year.toString() });
    } else {
      this.form.patchValue({ year: '2030' });
    }
    this.form.get('objective_value').setValue(this.objective.objectiveValue);
    
    this.perimeters$ = combineLatest([
      this.perimetersService.perimeters$,
      this.chartsService.getSiteConsumption(this.perimeter.id),
      this.scopeService.perimeterTypes$,
    ]).pipe(
      map(([perimeters, siteConsumption, perimeterTypes]) =>
        siteConsumption?.perimeters.filter((perimeter) => {
          const perimeterTypeId: number = perimeters
            .reduce((acc, p) => ([...acc, ...p.sub_perimeters]), [] as KPI.Perimeter[])
            .find((p) => p.id === perimeter.id)?.perimeter_type;
          return perimeterTypes[perimeterTypeId]?.perimeter_type_class === KPI.PerimeterTypeClass.CLASS_BUILDING;
        }),
      ),
      tap((filteredPerimeters) => {
        if (filteredPerimeters && filteredPerimeters.length === 1) {
          this.form.get('perimeter').setValue(filteredPerimeters[0].id);
        } else {
          const matchingPerimeter = filteredPerimeters?.find(p => p.name === this.objective.building);
          if (matchingPerimeter) {
            this.form.get('perimeter').setValue(matchingPerimeter.id);
          }
        }
      }),
    );
  }

  onSubmit(): void {
    if (this.form.valid) {
      this.loading = true;
      const selectedPerimeterId = parseInt(this.form.get('perimeter').value);
      
      const payload = {
        site_id: this.perimeter.id,
        sub_perimeter_id: selectedPerimeterId,
        year: parseInt(this.form.get('year').value),
        consumption: parseFloat(this.form.get('objective_value').value)
      };
      
      this.subscriptions.push(
        this.energyObjectivesService.updateEnergyObjective(Number(this.objective.id), payload).subscribe({
          next: (result) => {
            this.loading = false;
            this.dialogRef.close(result);
          },
          error: (error) => {
            this.loading = false;
            console.error('Error updating energy objective:', error);
            this.toastrService.danger(this.translate.instant('Error updating energy objective'), this.translate.instant('Error'));
          }
        })
      );
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
